import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useApp } from './store';
export function FeaturedCarousel() {
  const {
    config,
    featured
  } = useApp();
  const autoplay = config.modules.featuredAutoplay;
  const [index, setIndex] = useState(0);
  const count = featured.length;
  const go = useCallback((dir: number) => {
    setIndex(i => (i + dir + count) % count);
  }, [count]);
  useEffect(() => {
    if (!autoplay || count <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count, autoplay]);
  // Keep index in range if stories are removed
  useEffect(() => {
    if (index >= count && count > 0) setIndex(0);
  }, [count, index]);
  if (count === 0) return null;
  const story = featured[Math.min(index, count - 1)];
  return <section aria-label="Featured news" className="relative rounded-2xl overflow-hidden h-[340px] sm:h-[400px] bg-navy-800 shadow-sm" data-id="element-114">
      <AnimatePresence mode="wait" data-id="element-115">
        <motion.div key={story.id} initial={{
        opacity: 0,
        scale: 1.04
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-116">
          <img src={story.image} alt="" className="w-full h-full object-cover" data-id="element-117" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/70 to-navy-900/10" data-id="element-118" />
        </motion.div>
      </AnimatePresence>

      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8" data-id="element-119">
        <AnimatePresence mode="wait" data-id="element-120">
          <motion.div key={story.id + '-text'} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.4
        }} className="max-w-2xl" data-id="element-121">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-navy-900 bg-gold-500 px-3 py-1 rounded-full mb-4" data-id="element-122">
              {story.category}
            </span>
            <Link to={`/news/${story.id}`} className="block group" data-id="element-123">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white leading-tight mb-2 group-hover:text-gold-300 transition-colors" data-id="element-124">
                {story.title}
              </h2>
              <p className="text-navy-100 text-sm sm:text-base font-light mb-4 line-clamp-2" data-id="element-125">
                {story.excerpt}
              </p>
            </Link>
            <div className="text-xs text-navy-200 font-medium" data-id="element-126">
              {story.author} · {story.date}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      {count > 1 && <>
          <button onClick={() => go(-1)} aria-label="Previous story" className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur transition-colors" data-id="element-127">
            <ChevronLeft className="w-5 h-5" data-id="element-128" />
          </button>
          <button onClick={() => go(1)} aria-label="Next story" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur transition-colors" data-id="element-129">
            <ChevronRight className="w-5 h-5" data-id="element-130" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 right-6 flex gap-2" data-id="element-131">
            {featured.map((s, i) => <button key={s.id} onClick={() => setIndex(i)} aria-label={`Go to story ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-gold-500' : 'w-1.5 bg-white/50 hover:bg-white/80'}`} data-id="element-132" />)}
          </div>
        </>}
    </section>;
}